import { Server } from 'lucide-react'
import clsx from 'clsx'
import { AcceptanceSummary } from './AcceptanceSummary'
import { ComplianceBadge } from './PatchBadges'
import { COMPLIANCE_COPY, complianceNotes, effectiveCompliance } from '@/utils/patchStage'
import type { AcceptanceResult, PatchTargetResult, ValidationCheck } from '@/types/patching'
import styles from './Patching.module.css'

const RESULT_CLASS: Record<string, string> = {
  succeeded: styles.badgeDone,
  failed: styles.badgeFailed,
  skipped: styles.badgePending,
}

function ValidationList({ title, checks }: { title: string; checks: ValidationCheck[] }) {
  if (checks.length === 0) return null
  const passed = checks.filter((c) => c.passed).length
  return (
    <div data-testid={`validation-${title.toLowerCase()}`}>
      <strong>
        {title} validations ({passed}/{checks.length} passed)
      </strong>
      <ul className={styles.checkList}>
        {checks.map((c) => (
          <li key={c.check} className={c.passed ? styles.checkPass : styles.checkFail}>
            {c.passed ? 'PASS' : 'FAIL'} · <span className={styles.mono}>{c.check}</span>
            {c.detail ? ` — ${c.detail}` : ''}
          </li>
        ))}
      </ul>
    </div>
  )
}

function isDryRunInstallNote(value: string | null | undefined): boolean {
  const text = (value || '').toLowerCase()
  return text.includes('installpatches') || text.includes('live azure') || text.includes('not confirmed') || text.includes('dry run')
}

function ResultDetails({ result }: { result: PatchTargetResult }) {
  const checks = (result.validation ?? []).filter(
    (check) => !(result.dry_run && check.check === 'azure_install' && !check.passed && isDryRunInstallNote(check.detail)),
  )
  const error = result.dry_run && isDryRunInstallNote(result.error) ? null : result.error
  const skippedReason = result.dry_run && isDryRunInstallNote(result.skipped_reason) ? 'dry run — nothing was installed' : result.skipped_reason
  const patches = result.patches ?? []
  const applications = result.application_status ?? []
  const hasDetails =
    checks.length > 0 ||
    patches.length > 0 ||
    applications.length > 0 ||
    !!error ||
    !!skippedReason ||
    !!result.post_assessment ||
    result.unknown_state === true
  if (!hasDetails) return <span className={styles.muted}>—</span>
  return (
    <details className={styles.details}>
      <summary>Details</summary>
      <div className={styles.detailsBody}>
        {error && <div className={styles.checkFail}>Error: {error}</div>}
        {result.unknown_state && (
          <div className={styles.checkFail} data-testid="unknown-state">
            The install state of this VM is unknown. It was not retried automatically; check it before running again.
          </div>
        )}
        {skippedReason && <div>Skipped: {skippedReason}</div>}
        {result.maintenance_window_exceeded && (
          <div className={styles.checkFail}>The maintenance window was exceeded.</div>
        )}
        <ValidationList title="Mandatory" checks={checks.filter((c) => c.mandatory)} />
        <ValidationList title="Optional" checks={checks.filter((c) => !c.mandatory)} />
        {result.post_assessment && (
          <div data-testid="post-assessment">
            {result.post_assessment.missing ? (
              <>
                Post-assessment: {result.post_assessment.missing.critical} critical /{' '}
                {result.post_assessment.missing.security} security / {result.post_assessment.missing.other} other missing
              </>
            ) : (
              <>Post-assessment: not assessed (no update counts)</>
            )}
            {result.post_assessment.reboot_pending === null || result.post_assessment.reboot_pending === undefined
              ? ', reboot pending: unknown'
              : result.post_assessment.reboot_pending
                ? ', reboot pending'
                : ''}
          </div>
        )}
        {applications.length > 0 && (
          <div data-testid="application-status">
            <strong>Applications</strong>
            <ul className={styles.checkList}>
              {applications.map((app, i) => (
                <li key={`${i}-${app.application}`} data-app-state={app.state}>
                  <span className={styles.mono}>{app.application}</span> ({app.mechanism}) — {app.state}
                  {app.reason ? `: ${app.reason}` : ''}
                  {(app.state === 'unsupported' || app.state === 'unverified') && ' · not counted as compliant'}
                </li>
              ))}
            </ul>
          </div>
        )}
        {patches.length > 0 && (
          <div>
            <strong>Patches ({patches.length})</strong>
            <ul className={styles.checkList}>
              {patches.map((p, i) => (
                <li key={`${p.kb_or_package}-${i}`}>
                  <span className={styles.mono}>{p.kb_or_package}</span> {p.name} · {p.classification} · {p.state}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </details>
  )
}

export function ResultsTable({ results, acceptance }: { results: PatchTargetResult[]; acceptance?: AcceptanceResult | null }) {
  const states = new Set(results.map((r) => effectiveCompliance(r.compliance_state)))
  return (
    <section className={styles.section} aria-label="Per-VM results" data-testid="results-panel">
      <h3 className={styles.sectionTitle}>
        <Server size={14} /> Results &amp; validation
      </h3>
      <div className={styles.stack}>
        {acceptance && <AcceptanceSummary acceptance={acceptance} />}
        {results.length === 0 ? (
          <p className={styles.muted}>No per-VM results yet.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>VM</th>
                  <th>Batch</th>
                  <th>Outcome</th>
                  <th>Patches</th>
                  <th>Reboot</th>
                  <th>Compliance</th>
                  <th>Validation</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, index) => (
                  <tr key={`${r.vm_name}-${index}`} data-vm={r.vm_name} data-dry-run={r.dry_run ? 'true' : 'false'}>
                    <td>
                      <strong>{r.vm_name}</strong>
                      <span className={styles.cellSub}>{r.os_type}</span>
                    </td>
                    <td>{r.batch ?? '—'}</td>
                    <td>
                      <span className={clsx(styles.badge, RESULT_CLASS[r.status] ?? styles.badgePending)}>{r.status}</span>
                      {r.patch_status && <span className={styles.cellSub}>{r.patch_status}</span>}
                      {r.dry_run && <span className={styles.cellSub}>dry run — nothing installed</span>}
                    </td>
                    <td>
                      <span className={styles.cellSub}>
                        {r.installed} installed · {r.failed} failed · {r.pending} pending
                      </span>
                      <span className={styles.cellSub}>
                        {r.excluded} excluded · {r.not_selected} not selected
                      </span>
                    </td>
                    <td>{r.reboot_status ?? '—'}</td>
                    <td>
                      <ComplianceBadge state={r.compliance_state} />
                      {complianceNotes(r.compliance_notes).length > 0 && (
                        <ul className={styles.notesList} data-testid="vm-compliance-notes">
                          {complianceNotes(r.compliance_notes).map((note, i) => (
                            <li key={`${i}-${note}`}>{note}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td>
                      <ResultDetails result={r} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(states.has('unverified') || states.has('unsupported')) && (
          <div className={styles.legend} data-testid="compliance-legend">
            {states.has('unverified') && (
              <span>
                <strong>{COMPLIANCE_COPY.unverified.label}:</strong> {COMPLIANCE_COPY.unverified.explain}
              </span>
            )}
            {states.has('unsupported') && (
              <span>
                <strong>{COMPLIANCE_COPY.unsupported.label}:</strong> {COMPLIANCE_COPY.unsupported.explain}
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
